<template>
  <div class="bg-base-100 rounded-lg p-6">
    <div
      class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8"
    >
      <h2 class="text-xl font-semibold">Pavėžėjimų užsakymų sąrašas</h2>
      <div class="flex gap-2">
        <!-- DataFilter integration with reference data from store -->
        <DataFilter v-model="filters" title="Filtrai">
          <!-- Date range filters -->
          <div class="grid grid-cols-2 gap-2 mb-2">
            <fieldset class="fieldset">
              <legend class="fieldset-legend text-sm">Data nuo</legend>
              <input
                type="date"
                v-model="filters.date_from"
                class="input w-full"
              />
            </fieldset>
            <fieldset class="fieldset">
              <legend class="fieldset-legend text-sm">Data iki</legend>
              <input
                type="date"
                v-model="filters.date_to"
                class="input w-full"
              />
            </fieldset>
          </div>

          <!-- Status filter -->
          <fieldset class="fieldset mb-2">
            <legend class="fieldset-legend text-sm">Statusas</legend>
            <select
              v-model="filters.status"
              class="select w-full"
            >
              <option value="">Visi statusai</option>
              <option
                v-for="(config, status) in refStore.tripStatusConfig"
                :key="status"
                :value="status"
              >
                {{ config.text }}
              </option>
            </select>
          </fieldset>
        </DataFilter>

        <button
          class="btn btn-sm btn-outline"
          @click="handleExport"
          :disabled="tripStore.isExporting"
          title="Atsisiųsti sąrašą .xlsx formatu"
        >
          <DefaultIcon name="download" size="w-4 h-4" class="mr-1" />
        </button>

        <router-link
          to="/trips/create"
          class="btn btn-sm btn-success"
          title="Registruoti naują pavėžėjimą"
        >
          <DefaultIcon name="plus" size="w-4 h-4" />
          Naujas
        </router-link>
      </div>
    </div>

    <div v-if="tripStore.isLoading" class="text-center py-4">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <div v-else class="overflow-x-auto">
      <div v-if="trips.length > 0">
        <table class="table w-full mb-3">
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Data</th>
              <th>Pradžia</th>
              <th>Pabaiga</th>
              <th>Keleiviai</th>
              <th>Papildoma informacija</th>
              <th>Statusas</th>
              <th>Kelionė</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="trip in trips" :key="trip.id">
              <td>{{ trip.id }}</td>
              <td>{{ trip.date }}</td>
              <td>
                {{ trip.startWaypoint?.time }} <br />
                {{ trip.startWaypoint?.address }}
              </td>
              <td>
                {{ trip.endWaypoint?.time }}<br />
                {{ trip.endWaypoint?.address }}
              </td>
              <td>
                <div class="flex items-center gap-2 mt-1">
                  <div class="flex items-center gap-1">
                    <span>{{ trip.person_regular_count }}</span>
                    <DefaultIcon name="passenger" size="w-4 h-4" />
                  </div>
                  <div class="flex items-center gap-1">
                    <span>{{ trip.person_disabled_count }}</span>
                    <DefaultIcon name="wheelchair" size="w-4 h-4" />
                  </div>
                </div>
                <div class="text-xs mt-1">
                  <template
                    v-for="(client, index) in trip.clients"
                    :key="client.id"
                  >
                    <router-link
                      :to="`/clients/${client.id}/edit`"
                      class="hover:underline"
                    >
                      {{ client.name }} {{ client.surname }} </router-link
                    >{{ index < trip.clients.length - 1 ? ", " : "" }}
                  </template>
                </div>
              </td>
              <td>
                <div>
                  Pagrindas:
                  {{ refStore.tripBasisTypeConfig[trip.basis_type].text }}
                </div>
                <div v-if="trip.assistant_needed">Reikalingas palydėjimas</div>
                <div v-if="trip.child_seat_needed">
                  Reikalinga vaikiška kėdutė
                </div>
                <div v-if="trip.wheelchair_needed">
                  Reikalingas neįgaliojo vež.
                </div>
                <div v-if="trip.description">
                  Pavėžėjimo pastaba: {{ trip.description }}
                </div>
              </td>
              <td>
                <span
                  :class="[
                    'badge badge-sm',
                    refStore.tripStatusConfig[trip.status]?.class ||
                      'badge-ghost',
                  ]"
                >
                  {{
                    refStore.tripStatusConfig[trip.status]?.text || trip.status
                  }}
                </span>
              </td>
              <td class="text-xs">
                <div v-if="trip.travel?.id">
                  Kelionė nr.: {{ trip.travel.id }}<br />
                  Kelionės laikas: {{ trip.travel.date }}<br />
                  Transporto priemonė: {{ trip.travel.vehicle }}<br />
                  Organizuoja: {{ trip.organization.info }}
                </div>
                <div v-else>Pavėžėjimas neturi priskirtos kelionės</div>
              </td>
              <td>
                <div class="flex items-center gap-2">
                  <router-link
                    :to="'/trips/' + trip.id + '/view'"
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Peržiūrėti pavėžėjimo informacija"
                  >
                    <DefaultIcon name="magnifying" size="w-4 h-4" />
                  </router-link>
                  <router-link
                    v-if="trip.status == 'tripStatus3'"
                    :to="'/trips/' + trip.id + '/edit'"
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Redaguoti pavėžėjimo informacija"
                  >
                    <DefaultIcon name="editPencil" size="w-4 h-4" />
                  </router-link>
                  <button
                    v-if="
                      trip.status == 'tripStatus1' ||
                      trip.status == 'tripStatus6'
                    "
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Atšaukti pavėžėjimą"
                    @click="confirmCancelTrip(trip.id)"
                  >
                    <DefaultIcon name="xMark" size="w-4 h-4" />
                  </button>

                  <button
                    v-if="
                      trip.status == 'tripStatus1' ||
                      trip.status == 'tripStatus6'
                    "
                    class="btn btn-sm btn-ghost btn-circle"
                    title="Patvirtinti pavėžėjimo atlikimą"
                    @click="confirmCompleteTrip(trip.id)"
                  >
                    <DefaultIcon name="check" size="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <Pagination :meta="tripStore.meta" />
      </div>

      <div v-else>
        <div v-if="activeFiltersCount > 0">
          Pagal pasirinktus parametrus pavėžėjimų nerasta
        </div>
        <div v-else>
          Pavėžėjimų užsakymų dar nesukūrėte. Kurkite naują pavėžėjimo užsakymą
          paspaudę - Registruoti naują.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useTripStore } from "@/stores/trips";
import { useReferenceDataStore } from "@/stores/referenceData";
import { onMounted, computed, watch, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTimeManagement } from "@/composables/time/useTimeManagement";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Pagination from "@/components/ui/pagination/PaginationComponent.vue";
import DataFilter from "@/components/filters/DataFilter.vue";

const tripStore = useTripStore();
const refStore = useReferenceDataStore();

const route = useRoute();
const router = useRouter();
const { getTodayDate } = useTimeManagement();

// Extended filter values for v-model
const filters = ref({
  date_from: "",
  date_to: "",
  status: "",
});

// Calculate active filters count
const activeFiltersCount = computed(() => {
  return Object.values(filters.value).filter((val) => val).length;
});

const trips = computed(() =>
  tripStore.trips.map((trip) => ({
    ...trip,
    startWaypoint: trip.waypoints?.[0],
    endWaypoint: trip.waypoints?.[trip.waypoints?.length - 1],
  })),
);

// Watch for URL changes to fetch data
watch(
  () => route.query,
  async (newQuery) => {
    await tripStore.fetchTrips(newQuery);
  },
);

onMounted(async () => {
  try {
    // Ensure reference data is loaded
    if (!refStore.isInitialized) {
      await refStore.fetchReferenceData();
    }

    // Set default parameters if none exist
    if (!route.query.page && !route.query.status) {
      await router.replace({
        query: {
          page: "1",
          date_from: getTodayDate(),
        },
      });
    } else {
      // Pass the initial page from route if it exists
      await tripStore.fetchTrips(route.query);
    }
  } catch (error) {
    console.error("Failed to fetch travels:", error);
  }
});

// Cancel trip by setting travel id to null
const confirmCancelTrip = async (tripId) => {
  if (confirm("Ar jūs tikrai norite atšaukti pavėžėjimą")) {
    await handleCancelTrip(tripId);
  }
};

const handleCancelTrip = async (tripId) => {
  try {
    await tripStore.cancelTrip(tripId);
    await tripStore.fetchTrips(route.query);
  } catch (error) {
    console.error("Failed to cancel trip:", error);
  }
};

// Complete trip by setting travel id to null
const confirmCompleteTrip = async (tripId) => {
  if (confirm("Ar jūs tikrai norite patvirtinti, kad pavėžėjimas atliktas?")) {
    await handleCompleteTrip(tripId);
  }
};

const handleCompleteTrip = async (tripId) => {
  try {
    await tripStore.completeTrip(tripId);
    await tripStore.fetchTrips(route.query);
  } catch (error) {
    console.error("Failed to complete trip:", error);
  }
};

const handleExport = () => {
  tripStore.exportTrips(route.query);
};
</script>
